import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table } from '../components/ui/table';
import { useApiConfig } from '../hooks/useApiConfig';
import { BACKEND_BASE_URL } from '../config/backendConfig';
import { fetchDnVisibility } from '../utils/api';

const fmt = (n) => (n ?? 0).toLocaleString('en-IN');

/** The run window the "Last run" column counts, in IST. Shown so a zero
 *  column is readable as "no run in this window" rather than a broken page —
 *  dev and other quiet environments legitimately show zeros. */
export function formatCycle(cycle) {
  if (!cycle?.start || !cycle?.end) return null;
  const ist = (iso) => new Date(new Date(iso).getTime() + 5.5 * 3600 * 1000);
  const d = (x) => x.toISOString().slice(0, 16).replace('T', ' ');
  return `${d(ist(cycle.start))} → ${d(ist(cycle.end))} IST`;
}

// Exported for tests, following the CampaignDashboard convention of testing a
// component's pure helpers directly rather than rendering it.

export const SORTS = {
  REMAINING: { label: 'Most remaining', compare: (a, b) => b.remaining - a.remaining },
  LAST_RUN: { label: 'Fetched last run', compare: (a, b) => b.fetchedLastRun - a.fetchedLastRun },
  TOTAL: { label: 'Largest total', compare: (a, b) => b.total - a.total },
  WORST_PCT: { label: 'Lowest coverage', compare: (a, b) => pctFetched(a) - pctFetched(b) },
  CLIENT: { label: 'Client name', compare: (a, b) => (a.clientName || '').localeCompare(b.clientName || '') },
};

/** Whole percent fetched. A config with nothing in scope is 100% done, not 0. */
export function pctFetched({ total, fetched }) {
  if (!total) return 100;
  return Math.round((fetched / total) * 100);
}

/** clientName is null when an active config outlives its clients row, so the
 *  search guard must not assume a string. */
export function filterRows(rows, {
  portal = 'ALL', search = '', onlyPending = false, onlyIdle = false, sort = 'REMAINING',
} = {}) {
  const needle = search.trim().toLowerCase();
  const out = (rows || []).filter(r => {
    if (portal !== 'ALL' && r.portal !== portal) return false;
    if (onlyPending && r.remaining === 0) return false;
    if (onlyIdle && r.fetchedLastRun > 0) return false;
    if (needle && !(r.clientName || '').toLowerCase().includes(needle)) return false;
    return true;
  });
  const compare = (SORTS[sort] || SORTS.REMAINING).compare;
  return out.sort(compare);
}

/** One row per portal x source, over ALL rows — the client-level filters are
 *  deliberately not applied here, so the portal figures always mean "everything
 *  in scope" rather than "whatever is on screen". */
export function rollupByPortal(rows) {
  const byKey = new Map();
  for (const r of rows || []) {
    const key = `${r.portal}|${r.docSource}`;
    const acc = byKey.get(key) || {
      portal: r.portal, docSource: r.docSource,
      clients: 0, total: 0, fetched: 0, remaining: 0, fetchedLastRun: 0, idle: 0,
    };
    acc.clients += 1;
    acc.total += r.total;
    acc.fetched += r.fetched;
    acc.remaining += r.remaining;
    acc.fetchedLastRun += r.fetchedLastRun;
    acc.idle += (r.fetchedLastRun === 0 && r.remaining > 0) ? 1 : 0;
    byKey.set(key, acc);
  }
  return [...byKey.values()].sort((a, b) => b.remaining - a.remaining);
}

export function computeTotals(rows) {
  return (rows || []).reduce((acc, r) => ({
    total: acc.total + r.total,
    fetched: acc.fetched + r.fetched,
    remaining: acc.remaining + r.remaining,
    fetchedLastRun: acc.fetchedLastRun + r.fetchedLastRun,
    idle: acc.idle + (r.fetchedLastRun === 0 && r.remaining > 0 ? 1 : 0),
  }), { total: 0, fetched: 0, remaining: 0, fetchedLastRun: 0, idle: 0 });
}

export default function DnVisibilityPage() {
  const cfg = useApiConfig();
  const [rows, setRows] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portal, setPortal] = useState('ALL');
  const [search, setSearch] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);
  const [onlyIdle, setOnlyIdle] = useState(false);
  const [sort, setSort] = useState('REMAINING');
  const [view, setView] = useState('CLIENTS');

  const load = useCallback(async (signal) => {
    if (!cfg) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDnVisibility(BACKEND_BASE_URL, cfg);
      if (!signal?.aborted) { setRows(data.rows || []); setCycle(data.cycle || null); }
    } catch (e) {
      if (!signal?.aborted) { setError(e.message); setRows([]); }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [cfg]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const portals = useMemo(
    () => ['ALL', ...Array.from(new Set((rows || []).map(r => r.portal))).sort()],
    [rows],
  );

  const visible = useMemo(
    () => filterRows(rows, { portal, search, onlyPending, onlyIdle, sort }),
    [rows, portal, search, onlyPending, onlyIdle, sort],
  );

  const totals = useMemo(
    () => computeTotals(view === 'PORTAL' ? (rows || []) : visible),
    [view, rows, visible],
  );
  const portalRows = useMemo(() => rollupByPortal(rows || []), [rows]);

  return (
    <div className="page-content dnv">
      <header className="dnv-head">
        <div>
          <h1>DN Document Coverage</h1>
          <p className="dnv-sub">
            Per client and portal: how many DN documents we have fetched out of how many there are.
          </p>
          {formatCycle(cycle) && (
            <p className="dnv-cycle">Last run window: {formatCycle(cycle)}</p>
          )}
        </div>
        <div className="dnv-actions">
          <Button variant="ghost" size="sm" onClick={() => load()} disabled={loading}>
            <RefreshCw size={15} /> Refresh
          </Button>
        </div>
      </header>

      <div className="dnv-tabs" role="tablist">
        <button role="tab" aria-selected={view === 'CLIENTS'}
                className={`dnv-tab ${view === 'CLIENTS' ? 'is-active' : ''}`}
                onClick={() => setView('CLIENTS')}>
          By client
        </button>
        <button role="tab" aria-selected={view === 'PORTAL'}
                className={`dnv-tab ${view === 'PORTAL' ? 'is-active' : ''}`}
                onClick={() => setView('PORTAL')}>
          By portal
        </button>
      </div>

      <div className="dnv-stats">
        <div className="dnv-stat"><span className="dnv-stat-label">Total</span><span className="dnv-stat-value">{fmt(totals.total)}</span></div>
        <div className="dnv-stat"><span className="dnv-stat-label">Fetched</span><span className="dnv-stat-value">{fmt(totals.fetched)}</span></div>
        <div className="dnv-stat"><span className="dnv-stat-label">Remaining</span><span className="dnv-stat-value">{fmt(totals.remaining)}</span></div>
        <div className="dnv-stat"><span className="dnv-stat-label">Fetched last run</span><span className="dnv-stat-value">{fmt(totals.fetchedLastRun)}</span></div>
        <div className="dnv-stat"><span className="dnv-stat-label">Idle with backlog</span><span className="dnv-stat-value">{fmt(totals.idle)}</span></div>
      </div>

      {view === 'CLIENTS' && (
      <div className="dnv-filters">
        <Input placeholder="Search client…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={portal} onChange={e => setPortal(e.target.value)} aria-label="Portal">
          {portals.map(p => <option key={p} value={p}>{p === 'ALL' ? 'All portals' : p}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort by">
          {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <label className="dnv-check">
          <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} />
          Only with remaining
        </label>
        <label className="dnv-check">
          <input type="checkbox" checked={onlyIdle} onChange={e => setOnlyIdle(e.target.checked)} />
          Nothing fetched last run
        </label>
        <span className="dnv-muted">{visible.length} rows</span>
      </div>
      )}

      {error && <div className="dnv-alert">{error}</div>}
      {loading && !rows && <p className="dnv-muted">Loading…</p>}
      {view === 'CLIENTS' && rows && visible.length === 0 && !loading && <p className="dnv-muted">No rows match these filters.</p>}

      {view === 'CLIENTS' && visible.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Client</th><th>Portal</th><th>Source</th>
              <th className="num">Total</th><th className="num">Fetched</th><th className="num">Remaining</th>
              <th className="num">Last run</th><th className="num">%</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => {
              const pct = pctFetched(r);
              return (
                // BOLDFIT exists twice in clients (uid 9 and 14), so the key
                // must include clientId or the two rows collapse into one.
                <tr key={`${r.clientId}|${r.portal}|${r.docSource}`}>
                  <td>{r.clientName || `Client ${r.clientId}`} <span className="dnv-muted">#{r.clientId}</span></td>
                  <td>{r.portal}</td>
                  <td>{r.docSource === 'debit_note' ? 'DN' : 'Invoice'}</td>
                  <td className="num">{fmt(r.total)}</td>
                  <td className="num">{fmt(r.fetched)}</td>
                  <td className="num">{fmt(r.remaining)}</td>
                  <td className="num">
                    {r.fetchedLastRun > 0
                      ? <span className="dnv-lastrun">{fmt(r.fetchedLastRun)}</span>
                      : <span className="dnv-muted">—</span>}
                  </td>
                  <td className="num">
                    <span className={`dnv-pct ${pct === 100 ? 'is-done' : pct < 50 ? 'is-low' : ''}`}>{pct}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {view === 'PORTAL' && portalRows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Portal</th><th>Source</th>
              <th className="num">Clients</th>
              <th className="num">Total</th><th className="num">Fetched</th><th className="num">Remaining</th>
              <th className="num">Last run</th><th className="num">Idle</th><th className="num">%</th>
            </tr>
          </thead>
          <tbody>
            {portalRows.map(p => {
              const pct = pctFetched(p);
              return (
                <tr key={`${p.portal}|${p.docSource}`}>
                  <td>{p.portal}</td>
                  <td>{p.docSource === 'debit_note' ? 'DN' : 'Invoice'}</td>
                  <td className="num">{fmt(p.clients)}</td>
                  <td className="num">{fmt(p.total)}</td>
                  <td className="num">{fmt(p.fetched)}</td>
                  <td className="num">{fmt(p.remaining)}</td>
                  <td className="num">
                    {p.fetchedLastRun > 0
                      ? <span className="dnv-lastrun">{fmt(p.fetchedLastRun)}</span>
                      : <span className="dnv-muted">—</span>}
                  </td>
                  <td className="num">{fmt(p.idle)}</td>
                  <td className="num">
                    <span className={`dnv-pct ${pct === 100 ? 'is-done' : pct < 50 ? 'is-low' : ''}`}>{pct}%</span>
                  </td>
                </tr>
              );
            })}
            <tr className="dnv-total">
              <td>TOTAL</td><td />
              <td className="num">{fmt(portalRows.reduce((a, p) => a + p.clients, 0))}</td>
              <td className="num">{fmt(totals.total)}</td>
              <td className="num">{fmt(totals.fetched)}</td>
              <td className="num">{fmt(totals.remaining)}</td>
              <td className="num">{fmt(totals.fetchedLastRun)}</td>
              <td className="num">{fmt(totals.idle)}</td>
              <td className="num">{pctFetched(totals)}%</td>
            </tr>
          </tbody>
        </Table>
      )}
    </div>
  );
}
